import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress, Switch, TextField } from '@mui/material';
import './ComprobantesPago.css';
import { PharmacyTabs, PharmacySelect } from './shared/PharmacyUI';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';

// ── helpers ───────────────────────────────────────────────────────────────────
const fmtBool = v => v ? 'Sí' : 'No';

const Inp = React.forwardRef(({label,value,onChange,placeholder,mono,required,disabled,type='text'},ref)=><TextField className="cfg-input" inputRef={ref} label={label} value={value??''} onChange={onChange} placeholder={placeholder} required={required} disabled={disabled} type={type} fullWidth size="small" InputLabelProps={{shrink:true}} inputProps={{style:{fontFamily:mono?'monospace':'inherit'}}}/>);
const Sel = ({label,value,onChange,children,required,disabled})=><PharmacySelect label={label+(required?' *':'')} value={value} onChange={onChange} disabled={disabled} w="100%">{children}</PharmacySelect>;

// Switch estilo iOS
const SwToggle = ({label,sublabel,value,onChange})=><label className="cfg-toggle"><span><strong>{label}</strong>{sublabel&&<small>{sublabel}</small>}</span><Switch checked={!!value} onChange={e=>onChange(e.target.checked)} size="small" inputProps={{'aria-label':label}}/></label>;

// Checkbox CPE estilo badge
const CpeCheck = ({ label, value, onChange, color }) => (
  <Box className="pharmacy-action cfg-action" component="button" type="button" aria-pressed={!!value} onClick={()=>onChange(!value)} sx={{display:'inline-flex',alignItems:'center',gap:0.6,px:1.2,py:0.7,borderRadius:'10px',cursor:'pointer',border:`1.5px solid ${value?color:IOS.separator}`,bgcolor:value?`${color}10`:IOS.bg,transition:'all 0.15s',userSelect:'none'}}>
    <Box sx={{width:14,height:14,borderRadius:'4px',border:`2px solid ${value?color:IOS.separator}`,bgcolor:value?color:'transparent',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s',flexShrink:0}}>
      {value&&<svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>}
    </Box>
    <Typography sx={{fontSize:'0.75rem',fontWeight:value?700:500,color:value?color:IOS.label2}}>{label}</Typography>
  </Box>
);

const TH = ({children,center,right,w}) => (
  <th style={{padding:'9px 10px',width:w,textAlign:center?'center':right?'right':'left',fontSize:'0.6rem',fontWeight:700,color:IOS.label2,letterSpacing:'0.06em',textTransform:'uppercase',backgroundColor:IOS.surface,borderBottom:`1px solid ${IOS.separator}`,whiteSpace:'nowrap',position:'sticky',top:0,zIndex:1}}>{children}</th>
);

// ══════════════════════════════════════════════════════════════════════════════
const ConfiguracionCajas = ({ modulo = 'CAJA' }) => {   // 'CAJA' | 'DATAFARMA'
  const esFarma      = modulo === 'DATAFARMA';
  const rutaModulo   = esFarma ? '/datafarma' : '/caja';
  const navigate     = useNavigate();
  const user         = authService.getCurrentUser();
  const ueId         = user?.ueIdEstablecimiento||user?.UeIdEstablecimiento||0;
  const nf           = user?.nombreCompleto||user?.usuario||'';
  const handleLogout = ()=>{ authService.logout(); navigate('/login'); };

  const [social,setSocial]=useState(null);
  const [guardandoSocial,setGuardandoSocial]=useState(false);
  const [socialMsg,setSocialMsg]=useState('');
  useEffect(()=>{if(esFarma)return;api.get('/caja/configuracion/beneficio-social',{params:{ueId}}).then(r=>setSocial(r.data)).catch(()=>setSocialMsg('No se pudo cargar Beneficio Social. Verifica que la actualización SQL esté instalada.'));},[esFarma,ueId]);
  const guardarSocial=async(habilitado)=>{
    setGuardandoSocial(true);setSocialMsg('');
    try{await api.put('/caja/configuracion/beneficio-social',{habilitado},{params:{ueId}});setSocial(x=>({...x,habilitado}));setSocialMsg('Configuración guardada para esta ejecutora.');}
    catch(e){setSocialMsg(e.response?.data?.message||'No se pudo guardar la configuración.');}
    finally{setGuardandoSocial(false);}
  };
  const [tab,       setTab]       = useState(0); // 0=Listado, 1=Edicion
  const [cajas,     setCajas]     = useState([]);
  const [ipress,    setIpress]    = useState([]);
  const [cargando,  setCargando]  = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [busqueda,  setBusqueda]  = useState('');
  const [msgOk,     setMsgOk]     = useState('');
  const [msgErr,    setMsgErr]    = useState('');
  const [modoEditar,setModoEditar]= useState(false);

  // ── Formulario ──────────────────────────────────────────────────────────────
  const empty = {
    id:0, codigo:'', nombre:'', ubicacion:'', pcNombre:'', impresora:'',
    formatoImpresion:'A4', serie:'', copias:1, idEstablecimiento:'',
    // Switches
    previsualizacion:false, correoAuto:false, permiteAnulacion:true,
    serieManual:false, usaLector:false, ncDirecta:false, permiteDolares:false,
    // CPE
    cpeTicket:false, cpeBoleta:true, cpeFactura:false, cpeNc:false
  };
  const [form, setForm] = useState({...empty});
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  // ── Cargar datos ─────────────────────────────────────────────────────────────
  useEffect(()=>{
    cargarCajas();
    api.get('/caja/configuracion/ipress', { params:{ ueId } })
      .then(r=>{ if(r.data.success) setIpress(r.data.data||[]); })
      .catch(console.error);
  },[modulo]);

  const cargarCajas = async()=>{
    setCargando(true);
    try{
      const r = await api.get('/caja/configuracion/listar', { params:{ ueId, busqueda:busqueda||null, modulo } });
      if(r.data.success) setCajas(r.data.data||[]);
    }catch(e){console.error(e);}
    finally{setCargando(false);}
  };

  // ── Parsear tipos CPE ─────────────────────────────────────────────────────────
  const parsearCpe = (tipos='') => ({
    cpeTicket:  tipos.includes('TICKET'),
    cpeBoleta:  tipos.includes('BOLETA'),
    cpeFactura: tipos.includes('FACTURA'),
    cpeNc:      tipos.includes('NOTA_CREDITO'),
  });

  const armarCpe = (f) => {
    const partes = [];
    if(f.cpeBoleta)  partes.push('BOLETA');
    if(f.cpeFactura) partes.push('FACTURA');
    if(f.cpeTicket)  partes.push('TICKET');
    if(f.cpeNc)      partes.push('NOTA_CREDITO');
    return partes.join(',');
  };

  // ── Editar ───────────────────────────────────────────────────────────────────
  const editarCaja = (c) => {
    setForm({
      id:                 c.id,
      codigo:             c.codigo,
      nombre:             c.nombre,
      ubicacion:          c.ubicacion||'',
      pcNombre:           c.pcNombre||'',
      impresora:          c.impresora||'',
      formatoImpresion:   c.formatoImpresion||'A4',
      serie:              c.serie||'',
      copias:             c.copias||1,
      idEstablecimiento:  String(c.idEstablecimiento||''),
      previsualizacion:   c.previsualizacion,
      correoAuto:         c.correoAuto,
      permiteAnulacion:   c.permiteAnulacion,
      serieManual:        c.serieManual,
      usaLector:          c.usaLector,
      ncDirecta:          c.ncDirecta,
      permiteDolares:     !!c.permiteDolares,
      ...parsearCpe(c.tiposCpe),
    });
    setModoEditar(true);
    setMsgOk(''); setMsgErr('');
    setTab(1);
  };

  const nuevaCaja = () => {
    setForm({...empty});
    setModoEditar(false);
    setMsgOk(''); setMsgErr('');
    setTab(1);
  };

  // ── Guardar ───────────────────────────────────────────────────────────────────
  const guardar = async() => {
    if(!form.codigo.trim()||!form.nombre.trim()){setMsgErr('Codigo y Nombre son obligatorios');return;}
    if(!form.idEstablecimiento){setMsgErr('Selecciona una IPRESS');return;}
    setGuardando(true); setMsgErr(''); setMsgOk('');
    try{
      const payload = {
        id:                form.id,
        codigo:            form.codigo.trim(),
        nombre:            form.nombre.trim(),
        ubicacion:         form.ubicacion,
        pcNombre:          form.pcNombre,
        impresora:         form.impresora,
        formatoImpresion:  form.formatoImpresion,
        serie:             form.serie,
        copias:            parseInt(form.copias)||1,
        tiposCpe:          armarCpe(form),
        previsualizacion:  form.previsualizacion,
        correoAuto:        form.correoAuto,
        permiteAnulacion:  form.permiteAnulacion,
        serieManual:       form.serieManual,
        usaLector:         form.usaLector,
        ncDirecta:         form.ncDirecta,
        idEstablecimiento: parseInt(form.idEstablecimiento),
        modulo,                                   // la caja nace en este módulo
        permiteDolares:    !!form.permiteDolares,
      };
      const r = modoEditar
        ? await api.put('/caja/configuracion/actualizar', payload)
        : await api.post('/caja/configuracion/insertar', payload);

      if(r.data.success){
        setMsgOk(r.data.message);
        await cargarCajas();
        setTimeout(()=>{ setTab(0); setMsgOk(''); },1500);
      } else setMsgErr(r.data.message || 'Error al guardar');
    } catch(e) { setMsgErr(e?.response?.data?.message || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  // ── Filtro local ───────────────────────────────────────────────────────────────
  const cajasFiltradas = useMemo(()=>{
    if(!busqueda.trim()) return cajas;
    const b = busqueda.toLowerCase();
    return cajas.filter(c=>
      c.nombre?.toLowerCase().includes(b)||
      c.codigo?.toLowerCase().includes(b)||
      c.pcNombre?.toLowerCase().includes(b)||
      c.serie?.toLowerCase().includes(b)
    );
  },[cajas, busqueda]);

  return (
    <Box className="cfg-page pharmacy-page cpe-page" sx={{minHeight:'100vh',bgcolor:IOS.bg}}>
      <HeaderIOS titulo="Ajustes" subtitulo={esFarma?'Datafarma':'Caja'} onHome={()=>navigate(rutaModulo)} onLogout={handleLogout} nombreCompleto={nf} avatarLetra={nf.charAt(0).toUpperCase()}/>

      <Box className="pharmacy-content cpe-content" sx={{maxWidth:1600,mx:'auto',px:{xs:1.5,sm:2.5},pt:2,pb:8}}>

        <Box className="pharmacy-inline-tabs"><PharmacyTabs value={tab} onChange={i=>{if(i===tab)return;if(i===0){setTab(0);setMsgOk('');setMsgErr('');}else if(modoEditar)setTab(1);else nuevaCaja();}} options={['Cajas registradas',modoEditar?'Editar caja':'Nueva caja']}/></Box>
        <Box className="cpe-intro"><Typography className="cpe-eyebrow">Ajustes · {esFarma?'Datafarma':'Caja'}</Typography><Typography component="h1">{tab===0?'Cada caja, bien configurada':modoEditar?'Ajusta tu punto de atención':'Prepara una nueva caja'}</Typography><Typography>{tab===0?'Organiza tus equipos, comprobantes e impresión en un solo lugar.':'Define la identificación, el equipo y las opciones de esta caja.'}</Typography></Box>

        {!esFarma&&tab===0&&<Box sx={{mb:2,p:2.2,bgcolor:'#fff',border:'1px solid #E5EAF1',borderRadius:'18px'}}>
          <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:2}}><Box><Typography sx={{fontWeight:650,color:IOS.label}}>Beneficio Social · Atención gratuita</Typography><Typography sx={{fontSize:12,color:IOS.label2,mt:.5}}>Emitir constancia interna cuando el beneficio cubra el 100 %. Se aplica a las cajas de esta ejecutora.</Typography></Box>
          <Switch checked={!!social?.habilitado} disabled={!social?.puedeEditar||guardandoSocial} onChange={e=>guardarSocial(e.target.checked)} inputProps={{'aria-label':'Habilitar constancia de Beneficio Social'}}/></Box>
          <Typography sx={{fontSize:11,color:IOS.label2,mt:1}}>Solo para entidades públicas comprendidas en la excepción aplicable. No se envía a SUNAT. {social?.puedeEditar?'Activación exclusiva del administrador.':'Solo el administrador puede modificar esta opción.'}</Typography>
          {socialMsg&&<Typography role="status" sx={{fontSize:12,mt:1,color:IOS.blue}}>{socialMsg}</Typography>}
        </Box>}
        {/* ════ TAB 0 — LISTADO ════ */}
        {tab===0&&(
          <Box>
            {/* Barra de herramientas */}
            <Box sx={{display:'flex',gap:1.5,alignItems:'center',mb:2,flexWrap:'wrap'}}>
              <Box sx={{position:'relative',flex:1,minWidth:200}}>
                <Box sx={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:IOS.label2,pointerEvents:'none'}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                </Box>
                <input placeholder="Buscar por nombre, codigo, PC, serie..." value={busqueda} onChange={e=>setBusqueda(e.target.value)}
                  style={{width:'100%',height:36,paddingLeft:30,paddingRight:10,border:`1px solid ${IOS.separator}`,borderRadius:9,fontSize:'0.82rem',color:IOS.label,backgroundColor:IOS.surface,outline:'none',fontFamily:'inherit',boxSizing:'border-box'}}
                  onFocus={e=>e.target.style.borderColor=IOS.blue} onBlur={e=>e.target.style.borderColor=IOS.separator}/>
              </Box>
              <Box className="pharmacy-action cfg-action" component="button" onClick={cargarCajas} disabled={cargando}
                sx={{height:36,px:1.5,borderRadius:'9px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',display:'flex',alignItems:'center',gap:0.6}}>
                {cargando?<CircularProgress size={13} sx={{color:IOS.blue}}/>:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>}
                <Typography sx={{fontSize:'0.78rem',fontWeight:600,color:IOS.blue}}>Actualizar</Typography>
              </Box>
              <Box className="pharmacy-action cfg-action" component="button" onClick={nuevaCaja}
                sx={{height:36,px:1.5,borderRadius:'9px',border:'none',bgcolor:IOS.blue,cursor:'pointer',display:'flex',alignItems:'center',gap:0.6}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                <Typography sx={{fontSize:'0.78rem',fontWeight:700,color:'white'}}>Nueva caja</Typography>
              </Box>
              <Box sx={{px:1,py:0.3,bgcolor:IOS.bg,borderRadius:'20px',border:`1px solid ${IOS.separator}`}}>
                <Typography sx={{fontSize:'0.68rem',fontWeight:700,color:IOS.blue}}>{cajasFiltradas.length} cajas</Typography>
              </Box>
            </Box>

            {/* Tabla */}
            <Box className="cfg-surface" sx={{bgcolor:IOS.surface,borderRadius:'16px',border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)',overflow:'hidden'}}>
              <Box sx={{overflowX:'auto',overflowY:'auto',maxHeight:'calc(100vh - 260px)'}}>
                <table className="cfg-table"><thead><tr><TH>Caja / IPRESS</TH><TH>Estación</TH><TH>Impresión</TH><TH>Comprobantes</TH><TH>Moneda</TH><TH right>Acción</TH></tr></thead><tbody>
                  {cargando?<tr><td colSpan={6}><div className="cfg-empty"><CircularProgress size={24}/></div></td></tr>:!cajasFiltradas.length?<tr><td colSpan={6}><div className="cfg-empty">{busqueda?'No hay cajas que coincidan con la búsqueda.':'Aún no hay cajas registradas.'}</div></td></tr>:cajasFiltradas.map(c=><tr key={c.id}>
                    <td data-label="Caja / IPRESS"><span className="cfg-code">{c.codigo}</span><strong>{c.nombre}</strong><small>{c.nombreEstablecimiento||'Sin IPRESS registrada'}</small></td>
                    <td data-label="Estación"><strong>{c.pcNombre||'Sin equipo'}</strong><small>{c.ubicacion||'Sin ubicación registrada'}</small></td>
                    <td data-label="Impresión"><strong>{c.impresora||'Sin impresora'}</strong><small>{c.formatoImpresion||'A4'} · {c.copias||1} copia{Number(c.copias||1)!==1?'s':''}</small></td>
                    <td data-label="Comprobantes"><div className="cfg-badges">{(c.tiposCpe||'').split(',').filter(Boolean).map(t=><span key={t} className={'cfg-badge '+(t==='BOLETA'?'blue':t==='FACTURA'?'amber':t==='TICKET'?'purple':'green')}>{t.replace('NOTA_CREDITO','N. crédito')}</span>)}</div><small>Serie · {c.serie||'—'}</small></td>
                    <td data-label="Moneda"><span className={'cfg-badge '+(c.permiteDolares?'green':'neutral')}>{c.permiteDolares?'PEN / USD':'PEN'}</span></td>
                    <td data-label="Acción"><button className="cfg-edit" onClick={()=>editarCaja(c)} aria-label={'Editar '+c.nombre}>Editar <span aria-hidden="true">↗</span></button></td>
                  </tr>)}
                </tbody></table>
              </Box>
            </Box>
          </Box>
        )}

        {/* ════ TAB 1 — EDICION ════ */}
        {tab===1&&(
          <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',lg:'1fr 1fr'},gap:2,alignItems:'start'}}>

            {/* ── Columna izquierda — Datos basicos ── */}
            <Box sx={{display:'flex',flexDirection:'column',gap:2}}>

              {/* Identificacion */}
              <Box className="cfg-surface" sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2.5,border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
                <Box sx={{display:'flex',alignItems:'center',gap:1,mb:2,pb:1.5,borderBottom:`1px solid ${IOS.separator}`}}>
                  <Box sx={{width:32,height:32,borderRadius:'10px',bgcolor:`${IOS.blue}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                  </Box>
                  <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label}}>
                    {modoEditar ? `Editando: ${form.nombre}` : 'Nueva caja'}
                  </Typography>
                </Box>

                <Box sx={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:1.5,mb:1.5}}>
                  <Inp label="Código" value={form.codigo} onChange={e=>set('codigo',e.target.value)} placeholder="Ej: C001" required mono/>
                  <Inp label="Serie electrónica" value={form.serie} onChange={e=>set('serie',e.target.value)} placeholder="Ej: 001" mono/>
                </Box>
                <Box sx={{mb:1.5}}>
                  <Inp label="Nombre de la caja" value={form.nombre} onChange={e=>set('nombre',e.target.value)} placeholder="Ej: Caja Principal" required/>
                </Box>
                <Box sx={{mb:1.5}}>
                  <Inp label="Ubicación" value={form.ubicacion} onChange={e=>set('ubicacion',e.target.value)} placeholder="Ej: Primer piso, ventanilla 1"/>
                </Box>
                <Sel label="IPRESS asociada" value={form.idEstablecimiento} onChange={e=>set('idEstablecimiento',e.target.value)} required>
                  <option value="">Seleccionar IPRESS...</option>
                  {ipress.map(ip=><option key={ip.id} value={String(ip.id)}>{ip.nombre}</option>)}
                </Sel>
              </Box>

              {/* Estacion y hardware */}
              <Box className="cfg-surface" sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2.5,border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
                <Box sx={{display:'flex',alignItems:'center',gap:1,mb:2,pb:1.5,borderBottom:`1px solid ${IOS.separator}`}}>
                  <Box sx={{width:32,height:32,borderRadius:'10px',bgcolor:`${IOS.orange}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                  </Box>
                  <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label}}>Equipo e impresión</Typography>
                </Box>

                <Box sx={{mb:1.5}}>
                  <Inp label="Nombre del PC" value={form.pcNombre} onChange={e=>set('pcNombre',e.target.value)} placeholder="Ej: PC-CAJA-01 (nombre del equipo en red)" mono/>
                </Box>
                <Box sx={{mb:1.5}}>
                  <Inp label="Impresora" value={form.impresora} onChange={e=>set('impresora',e.target.value)} placeholder="Ej: HP LaserJet P1102w"/>
                  <Typography sx={{fontSize:'0.62rem',color:IOS.label2,mt:0.5}}>Ingresa el nombre exacto de la impresora tal como aparece en el sistema operativo.</Typography>
                </Box>
                <Box sx={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:1.5}}>
                  <Sel label="Formato de impresión" value={form.formatoImpresion} onChange={e=>set('formatoImpresion',e.target.value)}>
                    <option value="A4">A4</option>
                    <option value="TICKET">Ticket 80mm</option>
                    <option value="CARTA">Carta</option>
                  </Sel>
                  <Box>
                    <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.5}}>N de copias</Typography>
                    <Box sx={{display:'flex',alignItems:'center',gap:1}}>
                      <Box component="button" type="button" className="cfg-step" aria-label="Reducir copias" disabled={form.copias<=1} onClick={()=>set('copias',Math.max(1,form.copias-1))} sx={{width:34,height:34,borderRadius:'50%',bgcolor:IOS.bg,border:`1px solid ${IOS.separator}`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer','&:hover':{bgcolor:'#E5E5EA'}}}>
                        <svg width="12" height="2" viewBox="0 0 12 2" fill="none"><path d="M1 1h10" stroke={IOS.label2} strokeWidth="1.5" strokeLinecap="round"/></svg>
                      </Box>
                      <Typography sx={{fontSize:'1.1rem',fontWeight:800,color:IOS.label,minWidth:28,textAlign:'center'}}>{form.copias}</Typography>
                      <Box component="button" type="button" className="cfg-step" aria-label="Aumentar copias" disabled={form.copias>=10} onClick={()=>set('copias',Math.min(10,form.copias+1))} sx={{width:34,height:34,borderRadius:'50%',bgcolor:IOS.bg,border:`1px solid ${IOS.separator}`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer','&:hover':{bgcolor:'#E5E5EA'}}}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke={IOS.label2} strokeWidth="1.5" strokeLinecap="round"/></svg>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* CPE habilitados */}
              <Box className="cfg-surface" sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2.5,border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
                <Box sx={{display:'flex',alignItems:'center',gap:1,mb:2,pb:1.5,borderBottom:`1px solid ${IOS.separator}`}}>
                  <Box sx={{width:32,height:32,borderRadius:'10px',bgcolor:`${IOS.green}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </Box>
                  <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label}}>Tipos CPE habilitados</Typography>
                </Box>
                <Box sx={{display:'flex',gap:1,flexWrap:'wrap'}}>
                  <CpeCheck label="BOLETA"        value={form.cpeBoleta}  onChange={v=>set('cpeBoleta',v)}  color={IOS.blue}/>
                  <CpeCheck label="FACTURA"       value={form.cpeFactura} onChange={v=>set('cpeFactura',v)} color={IOS.orange}/>
                  <CpeCheck label="TICKET"        value={form.cpeTicket}  onChange={v=>set('cpeTicket',v)}  color={IOS.purple}/>
                  <CpeCheck label="NOTA CREDITO"  value={form.cpeNc}      onChange={v=>set('cpeNc',v)}      color={IOS.green}/>
                </Box>
                {armarCpe(form)&&(
                  <Box sx={{mt:1.5,px:1.2,py:0.8,bgcolor:IOS.bg,borderRadius:'9px',border:`1px solid ${IOS.separator}`}}>
                    <Typography sx={{fontSize:'0.65rem',color:IOS.label2}}>Tipos guardados: <strong style={{color:IOS.label}}>{armarCpe(form)}</strong></Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ── Columna derecha — Switches ── */}
            <Box sx={{display:'flex',flexDirection:'column',gap:2}}>

              {/* Opciones de impresion */}
              <Box className="cfg-surface" sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2.5,border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
                <Box sx={{display:'flex',alignItems:'center',gap:1,mb:1.5,pb:1.5,borderBottom:`1px solid ${IOS.separator}`}}>
                  <Box sx={{width:32,height:32,borderRadius:'10px',bgcolor:`${IOS.purple}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8" rx="1"/></svg>
                  </Box>
                  <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label}}>Preferencias de impresión</Typography>
                </Box>
                <SwToggle label="Vista previa" sublabel="Muestra una vista previa antes de imprimir" value={form.previsualizacion} onChange={v=>set('previsualizacion',v)}/>
                <SwToggle label="Correo automático" sublabel="Envia el comprobante por correo al paciente" value={form.correoAuto} onChange={v=>set('correoAuto',v)}/>
                <SwToggle label="Serie manual" sublabel="Permite ingresar la serie manualmente" value={form.serieManual} onChange={v=>set('serieManual',v)}/>
                <SwToggle label="Lector de codigo" sublabel="Habilita el uso de lector de codigos de barras" value={form.usaLector} onChange={v=>set('usaLector',v)}/>
              </Box>

              {/* Opciones de operacion */}
              <Box className="cfg-surface" sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2.5,border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
                <Box sx={{display:'flex',alignItems:'center',gap:1,mb:1.5,pb:1.5,borderBottom:`1px solid ${IOS.separator}`}}>
                  <Box sx={{width:32,height:32,borderRadius:'10px',bgcolor:`${IOS.red}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                  </Box>
                  <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label}}>Operación y permisos</Typography>
                </Box>
                <SwToggle label="Permitir anulaciones" sublabel="La caja puede anular ventas con token de autorizacion" value={form.permiteAnulacion} onChange={v=>set('permiteAnulacion',v)}/>
                <Box sx={{borderBottom:'none'}}>
                  <SwToggle label="Nota de credito directa" sublabel="Genera NC sin pasar por el flujo de anulacion" value={form.ncDirecta} onChange={v=>set('ncDirecta',v)}/>
                </Box>
                <SwToggle label="Ventas en dólares" sublabel="Muestra la opcion de vender en dolares (USD) en esta caja" value={form.permiteDolares} onChange={v=>set('permiteDolares',v)}/>
              </Box>

              {/* Resumen */}
              <Box className="cfg-summary" sx={{bgcolor:IOS.bg,borderRadius:'14px',p:2,border:`1px solid ${IOS.separator}`}}>
                <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:1.2}}>Resumen de configuracion</Typography>
                {[
                  ['Codigo',       form.codigo||'—'],
                  ['Nombre',       form.nombre||'—'],
                  ['Serie',        form.serie||'—'],
                  ['PC',           form.pcNombre||'—'],
                  ['Impresora',    form.impresora||'—'],
                  ['Formato',      form.formatoImpresion||'—'],
                  ['Copias',       String(form.copias)],
                  ['Anulacion',    fmtBool(form.permiteAnulacion)],
                  ['NC directa',   fmtBool(form.ncDirecta)],
                ].map(([k,v])=>(
                  <Box key={k} sx={{display:'flex',justifyContent:'space-between',py:0.5,borderBottom:`1px solid ${IOS.separator}`}}>
                    <Typography sx={{fontSize:'0.72rem',color:IOS.label2}}>{k}</Typography>
                    <Typography sx={{fontSize:'0.72rem',fontWeight:600,color:IOS.label,fontFamily:['Codigo','Serie','PC'].includes(k)?'monospace':'inherit'}}>{v}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Mensajes y boton guardar */}
              {(msgOk||msgErr)&&(
                <Box role={msgErr?"alert":"status"} sx={{p:1.5,borderRadius:'12px',bgcolor:msgOk?'#EDFAF2':'#FFF0EF',border:`1px solid ${msgOk?'#B7EFD0':'#FFD4D1'}`}}>
                  <Typography sx={{fontSize:'0.82rem',fontWeight:600,color:msgOk?'#1A6B3C':'#C0392B'}}>{msgErr||msgOk}</Typography>
                </Box>
              )}

              <Box className="cfg-save-bar" sx={{display:'flex',gap:1}}>
                <Box className="pharmacy-action cfg-action" component="button" onClick={()=>{setTab(0);setMsgOk('');setMsgErr('');}}
                  sx={{flex:1,height:44,borderRadius:'12px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer'}}>
                  <Typography sx={{fontSize:'0.85rem',fontWeight:600,color:IOS.label2}}>Cancelar</Typography>
                </Box>
                <Box className="pharmacy-action cfg-action" component="button" onClick={guardar} disabled={guardando}
                  sx={{flex:2,height:44,borderRadius:'12px',border:'none',bgcolor:guardando?'#E5E5EA':IOS.blue,cursor:guardando?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:1,boxShadow:`0 4px 14px ${IOS.blue}30`}}>
                  {guardando&&<CircularProgress size={15} sx={{color:'white'}}/>}
                  <Typography sx={{fontSize:'0.9rem',fontWeight:800,color:guardando?'#8E8E93':'white'}}>
                    {guardando?'Guardando...':(modoEditar?'Actualizar caja':'Registrar caja')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <style>{`
.cfg-page .cfg-surface{border-radius:20px!important;border-color:#e2e7ef!important;box-shadow:0 3px 12px #26365305!important}
.cfg-page .cfg-action{min-height:40px;border-radius:10px!important;box-shadow:none!important}
.cfg-page .cfg-input{margin-top:6px}.cfg-input .MuiOutlinedInput-root{border-radius:10px;font-size:14px}.cfg-input .MuiInputLabel-root{font-size:13px}.cfg-input .MuiOutlinedInput-notchedOutline{border-color:#dce3ed}
.cfg-toggle{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:16px 0;border-bottom:1px solid #edf0f5;cursor:pointer}.cfg-toggle:last-child{border:0}.cfg-toggle strong{display:block;color:#39485e;font-size:13px;font-weight:600}.cfg-toggle small{display:block;font-size:11px;line-height:1.6;color:#8894a5;margin-top:5px}.cfg-toggle .MuiSwitch-root{flex-shrink:0}
.cfg-table{width:100%;border-collapse:collapse;min-width:850px}.cfg-table td{padding:18px 16px;vertical-align:middle;font-size:12px;color:#65758d}.cfg-table td strong{display:block;color:#34445d;font-size:13px;font-weight:600;line-height:1.5;overflow-wrap:anywhere}.cfg-table td small{display:block;margin-top:5px;font-size:11px;line-height:1.6;color:#8592a4;overflow-wrap:anywhere}.cfg-table td:first-child{width:25%}.cfg-table td:nth-child(3){max-width:220px}.cfg-table tr:nth-child(even){background:#fafbfd}.cfg-table tbody tr:hover{background:#f3f7fc}
.cfg-code{display:inline-block;color:#6385b1;background:#edf3fb;border-radius:6px;padding:3px 7px;margin-bottom:7px;font-size:10px;font-weight:600}.cfg-badges{display:flex;gap:5px;flex-wrap:wrap}.cfg-badge{display:inline-flex;padding:4px 7px;border-radius:7px;font-size:10px;font-weight:600;white-space:nowrap}.cfg-badge.blue{color:#5484bc;background:#edf3fc}.cfg-badge.amber{color:#b28b44;background:#fff5e5}.cfg-badge.purple{color:#8f70b5;background:#f3eefb}.cfg-badge.green{color:#4f9775;background:#edf8f1}.cfg-badge.neutral{color:#8191a5;background:#f0f3f7}
.cfg-edit{display:inline-flex;gap:12px;align-items:center;border:1px solid #dce6f3;border-radius:10px;min-height:38px;padding:8px 12px;background:#eff5fd;color:#527eaf;font:inherit;font-size:12px;font-weight:600;cursor:pointer}
.cfg-empty{min-height:200px;display:flex;align-items:center;justify-content:center;color:#8592a5;font-size:13px}
.cfg-page .cfg-step{border-radius:10px!important;min-width:38px;min-height:38px;padding:0}.cfg-step:disabled{opacity:.4}
.cfg-summary{display:grid;grid-template-columns:1fr 1fr;gap:10px 20px;border-radius:20px!important;background:#eef3f9!important}.cfg-summary>p{grid-column:1/-1;margin-bottom:4px}.cfg-summary>div{display:flex;flex-direction:column;align-items:flex-start;gap:4px;border:0!important;min-width:0}.cfg-summary>div p:last-child{overflow-wrap:anywhere}
.cfg-save-bar{padding:14px;background:#fff;border:1px solid #e2e7ef;border-radius:16px}
@media(max-width:899px){.cfg-table{min-width:0}.cfg-table thead{display:none}.cfg-table tbody,.cfg-table tr,.cfg-table td{display:block}.cfg-table tbody tr{padding:16px}.cfg-table td{padding:8px 0!important;width:auto!important;max-width:none!important}.cfg-table td:not(:first-child)::before{content:attr(data-label);display:block;color:#8c98a9;font-size:10px;margin-bottom:5px}.cfg-table td:last-child{padding-top:12px!important}.cfg-edit{width:100%;justify-content:space-between}.cfg-table td[colspan]::before{display:none}}
`}</style>
    </Box>
  );
};

export default ConfiguracionCajas;
